import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Globe, CheckCircle, XCircle, ShoppingCart, Shield, Zap, Server } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useAppContext, TLD } from '../context/AppContext';
export function Domains() {
  const {
    tlds,
    addToCart
  } = useAppContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [activeTab, setActiveTab] = useState<'popular' | 'new' | 'country' | 'specialty'>('popular');
  // Simulated search results
  const [searchResults, setSearchResults] = useState<Array<{
    domain: string;
    tld: TLD;
    available: boolean;
  }>>([]);
  const activeTlds = tlds.filter(t => t.active);
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setIsSearching(true);
    setHasSearched(true);
    // Clean up search query (remove http://, www., and existing TLDs if present)
    let baseName = searchQuery.toLowerCase().trim();
    baseName = baseName.replace(/^(https?:\/\/)?(www\.)?/, '');
    const parts = baseName.split('.');
    if (parts.length > 1) {
      baseName = parts[0]; // Just take the name part
    }
    // Simulate API call delay
    setTimeout(() => {
      // Generate results for popular TLDs and the exact searched TLD if applicable
      const results = activeTlds.filter(t => t.category === 'popular' || t.category === 'new').slice(0, 8).map(tld => ({
        domain: `${baseName}${tld.extension}`,
        tld,
        // Simulate availability (70% chance available)
        available: Math.random() > 0.3
      }));
      // Sort so available domains are first, and .com is at the top if available
      results.sort((a, b) => {
        if (a.tld.extension === '.com') return -1;
        if (b.tld.extension === '.com') return 1;
        if (a.available === b.available) return 0;
        return a.available ? -1 : 1;
      });
      setSearchResults(results);
      setIsSearching(false);
    }, 1500);
  };
  const handleAddToCart = (domain: string, tld: TLD) => {
    addToCart({
      packageId: `dom_${tld.id}_${Date.now()}`,
      name: `${domain} Registration`,
      category: 'domain',
      price: tld.registerPrice,
      billing: 'yearly',
      quantity: 1
    });
    // Show a quick visual feedback (could use a toast here)
    alert(`${domain} added to cart!`);
  };
  const handleQuickPick = (extension: string) => {
    setSearchQuery(`mywebsite${extension}`);
  };
  return <div className="min-h-screen bg-slate-950 flex flex-col" data-id="element-1790">
      <Navbar data-id="element-1791" />

      <main className="flex-1 pt-24" data-id="element-1792">
        {/* Hero Search Section */}
        <section className="relative py-20 overflow-hidden" data-id="element-1793">
          {/* Background Elements */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-5xl h-full overflow-hidden -z-10" data-id="element-1794">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-fox-500/20 rounded-full blur-[120px]" data-id="element-1795" />
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-[120px]" data-id="element-1796" />
          </div>

          <div className="container mx-auto px-4 md:px-6 relative z-10 text-center max-w-4xl" data-id="element-1797">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-1798">
              <h1 className="text-4xl md:text-6xl font-heading font-bold text-white mb-6 leading-tight" data-id="element-1799">
                Find Your Perfect{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-fox-500 to-amber-400" data-id="element-1800">
                  Domain
                </span>
              </h1>
              <p className="text-lg md:text-xl text-slate-400 mb-10 max-w-2xl mx-auto" data-id="element-1801">
                Secure your online identity today. Search across hundreds of
                extensions to find the perfect name for your next big idea.
              </p>

              <form onSubmit={handleSearch} className="relative max-w-3xl mx-auto mb-8" data-id="element-1802">
                <div className="relative flex items-center" data-id="element-1803">
                  <Globe className="absolute left-6 text-slate-500 w-6 h-6" data-id="element-1804" />
                  <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search for your domain name..." className="w-full bg-slate-900/80 backdrop-blur-md border-2 border-slate-800 rounded-full py-5 pl-16 pr-40 text-lg text-white focus:border-fox-500 focus:outline-none transition-colors shadow-2xl" data-id="element-1805" />
                  <button type="submit" disabled={isSearching || !searchQuery.trim()} className="absolute right-3 bg-fox-600 hover:bg-fox-500 disabled:opacity-50 text-white px-8 py-3 rounded-full font-bold transition-colors flex items-center gap-2" data-id="element-1806">
                    {isSearching ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1807" /> : <Search size={20} data-id="element-1808" />}
                    Search
                  </button>
                </div>
              </form>

              <div className="flex flex-wrap items-center justify-center gap-3 text-sm" data-id="element-1809">
                <span className="text-slate-500 font-medium" data-id="element-1810">Popular:</span>
                {['.com', '.net', '.io', '.dev', '.co'].map(ext => <button key={ext} onClick={() => handleQuickPick(ext)} className="px-3 py-1 rounded-full bg-slate-900 border border-slate-800 text-slate-300 hover:border-fox-500 hover:text-fox-500 transition-colors" data-id="element-1811">
                    {ext}
                  </button>)}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Search Results */}
        <AnimatePresence data-id="element-1812">
          {hasSearched && <motion.section initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} className="py-12 bg-slate-900/50 border-y border-slate-800" data-id="element-1813">
              <div className="container mx-auto px-4 md:px-6 max-w-4xl" data-id="element-1814">
                <h2 className="text-2xl font-bold text-white mb-6" data-id="element-1815">
                  Search Results
                </h2>

                {isSearching ? <div className="space-y-4" data-id="element-1816">
                    {[1, 2, 3].map(i => <div key={i} className="bg-slate-900 border border-slate-800 rounded-2xl p-6 animate-pulse flex justify-between items-center" data-id="element-1817">
                        <div className="h-6 bg-slate-800 rounded w-48" data-id="element-1818" />
                        <div className="h-10 bg-slate-800 rounded w-32" data-id="element-1819" />
                      </div>)}
                  </div> : <div className="space-y-4" data-id="element-1820">
                    {searchResults.map((result, i) => <motion.div key={result.domain} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: i * 0.1
              }} className={`bg-slate-950 border ${result.available ? 'border-emerald-500/30 hover:border-emerald-500/50' : 'border-slate-800 opacity-75'} rounded-2xl p-4 md:p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 transition-colors`} data-id="element-1821">
                        <div className="flex items-center gap-4" data-id="element-1822">
                          {result.available ? <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-500 shrink-0" data-id="element-1823">
                              <CheckCircle size={24} data-id="element-1824" />
                            </div> : <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center text-red-500 shrink-0" data-id="element-1825">
                              <XCircle size={24} data-id="element-1826" />
                            </div>}
                          <div data-id="element-1827">
                            <h3 className={`text-xl font-bold ${result.available ? 'text-white' : 'text-slate-400 line-through decoration-red-500/50'}`} data-id="element-1828">
                              {result.domain}
                            </h3>
                            <div className="flex items-center gap-3 mt-1" data-id="element-1829">
                              <span className={`text-sm font-medium ${result.available ? 'text-emerald-500' : 'text-red-500'}`} data-id="element-1830">
                                {result.available ? 'Available' : 'Taken'}
                              </span>
                              {result.available && result.tld.whoisPrivacy && <span className="text-xs text-slate-500 flex items-center gap-1" data-id="element-1831">
                                  <Shield size={12} data-id="element-1832" /> Free Privacy
                                </span>}
                            </div>
                          </div>
                        </div>

                        {result.available && <div className="flex items-center justify-between md:justify-end gap-6 border-t md:border-t-0 border-slate-800 pt-4 md:pt-0" data-id="element-1833">
                            <div className="text-right" data-id="element-1834">
                              <div className="text-2xl font-bold text-white" data-id="element-1835">
                                ${result.tld.registerPrice}
                              </div>
                              <div className="text-xs text-slate-500" data-id="element-1836">
                                /first year
                              </div>
                            </div>
                            <button onClick={() => handleAddToCart(result.domain, result.tld)} className="bg-fox-600 hover:bg-fox-500 text-white px-6 py-3 rounded-xl font-medium transition-colors flex items-center gap-2" data-id="element-1837">
                              <ShoppingCart size={18} data-id="element-1838" /> Add
                            </button>
                          </div>}
                      </motion.div>)}
                  </div>}
              </div>
            </motion.section>}
        </AnimatePresence>

        {/* TLD Pricing Grid */}
        <section className="py-20" data-id="element-1839">
          <div className="container mx-auto px-4 md:px-6" data-id="element-1840">
            <div className="text-center mb-12" data-id="element-1841">
              <h2 className="text-3xl font-heading font-bold text-white mb-4" data-id="element-1842">
                Explore Extensions
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto" data-id="element-1843">
                We offer hundreds of top-level domains at competitive prices.
                All domains include free DNS management and email forwarding.
              </p>
            </div>

            {/* Category Tabs */}
            <div className="flex flex-wrap justify-center gap-2 mb-12" data-id="element-1844">
              {(['popular', 'new', 'country', 'specialty'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${activeTab === tab ? 'bg-fox-600 text-white shadow-lg shadow-fox-500/20' : 'bg-slate-900 text-slate-400 hover:text-white hover:bg-slate-800'}`} data-id="element-1845">
                    {tab === 'new' ? 'New & Tech' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>)}
            </div>

            {/* Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1846">
              {activeTlds.filter(t => t.category === activeTab).map((tld, i) => <motion.div key={tld.id} initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: i * 0.05
            }} className="bg-slate-950 border border-slate-800 rounded-2xl p-6 hover:border-fox-500/50 transition-colors group" data-id="element-1847">
                    <div className="flex justify-between items-start mb-6" data-id="element-1848">
                      <h3 className="text-3xl font-bold text-white group-hover:text-fox-500 transition-colors" data-id="element-1849">
                        {tld.extension}
                      </h3>
                      {tld.whoisPrivacy && <span className="bg-slate-900 text-slate-400 text-[10px] px-2 py-1 rounded font-medium flex items-center gap-1 border border-slate-800" data-id="element-1850">
                          <Shield size={10} data-id="element-1851" /> Privacy
                        </span>}
                    </div>

                    <div className="space-y-3 mb-6" data-id="element-1852">
                      <div className="flex justify-between items-end" data-id="element-1853">
                        <span className="text-sm text-slate-500" data-id="element-1854">Register</span>
                        <div className="text-right" data-id="element-1855">
                          <span className="text-xl font-bold text-white" data-id="element-1856">
                            ${tld.registerPrice}
                          </span>
                          <span className="text-xs text-slate-500" data-id="element-1857">/yr</span>
                        </div>
                      </div>
                      <div className="flex justify-between items-end" data-id="element-1858">
                        <span className="text-sm text-slate-500" data-id="element-1859">Renew</span>
                        <div className="text-right" data-id="element-1860">
                          <span className="text-sm font-medium text-slate-300" data-id="element-1861">
                            ${tld.renewPrice}
                          </span>
                          <span className="text-xs text-slate-500" data-id="element-1862">/yr</span>
                        </div>
                      </div>
                      <div className="flex justify-between items-end" data-id="element-1863">
                        <span className="text-sm text-slate-500" data-id="element-1864">Transfer</span>
                        <div className="text-right" data-id="element-1865">
                          <span className="text-sm font-medium text-slate-300" data-id="element-1866">
                            ${tld.transferPrice}
                          </span>
                        </div>
                      </div>
                    </div>

                    <button onClick={() => {
                setSearchQuery(`mywebsite${tld.extension}`);
                window.scrollTo({
                  top: 0,
                  behavior: 'smooth'
                });
              }} className="w-full bg-slate-900 hover:bg-fox-600 text-white py-3 rounded-xl font-medium transition-colors" data-id="element-1867">
                      Search {tld.extension}
                    </button>
                  </motion.div>)}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-20 bg-slate-900/50 border-t border-slate-800" data-id="element-1868">
          <div className="container mx-auto px-4 md:px-6" data-id="element-1869">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1870">
              <div className="text-center" data-id="element-1871">
                <div className="w-16 h-16 mx-auto bg-fox-500/10 text-fox-500 rounded-2xl flex items-center justify-center mb-6" data-id="element-1872">
                  <Shield size={32} data-id="element-1873" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3" data-id="element-1874">
                  Free WHOIS Privacy
                </h3>
                <p className="text-slate-400" data-id="element-1875">
                  Keep your personal information hidden from spammers and
                  scammers on eligible domains.
                </p>
              </div>
              <div className="text-center" data-id="element-1876">
                <div className="w-16 h-16 mx-auto bg-amber-500/10 text-amber-500 rounded-2xl flex items-center justify-center mb-6" data-id="element-1877">
                  <Zap size={32} data-id="element-1878" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3" data-id="element-1879">
                  Instant Setup
                </h3>
                <p className="text-slate-400" data-id="element-1880">
                  Your domain is registered and ready to use instantly after
                  checkout completes.
                </p>
              </div>
              <div className="text-center" data-id="element-1881">
                <div className="w-16 h-16 mx-auto bg-emerald-500/10 text-emerald-500 rounded-2xl flex items-center justify-center mb-6" data-id="element-1882">
                  <Server size={32} data-id="element-1883" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3" data-id="element-1884">
                  Advanced DNS
                </h3>
                <p className="text-slate-400" data-id="element-1885">
                  Full control over your DNS records with our powerful, globally
                  distributed nameservers.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-1886" />
    </div>;
}